import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { useNavigate } from 'react-router-dom';
import {
  Card, Typography, Button, Space, Tag, Select, Input, RadioGroup, Switch, DatePicker,
  Modal, Divider, Alert, Descriptions, EmptyState, Popconfirm, Row, Col, message,
} from '../../ui';
import {
  LogIn, LogOut, MapPin, ShoppingCart,
  MessageSquare, Wrench, CheckCircle2, Plus, Wallet,
  Clock, Navigation, Flag, Ban, AlertTriangle, Camera, ChevronDown, ChevronRight, MapPin as MapPinIcon,
} from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { request } from '../../api/client';
import { useAuthStore, usePermission } from '../../store/auth';
import { formatIst, formatIstTime, toIstDateString } from '../../lib/time';
import { FarmerFormModal } from '../farmers/FarmerFormModal';
import { FileUploadField } from '../../components/FileUploadField';
import { DayExpenseModal } from '../expense/DayExpenseModal';
import { MyRegularizationsCard } from './regularization';
import { MyAttendanceHistoryCard } from './MyAttendanceHistory';
import { getDeviceFix, type DeviceFix } from '../../hooks/useDeviceFix';
import { VisitPhotoCapture } from '../../components/VisitPhotoCapture';
import { inr2 } from '../../lib/format';
import { fileLabel } from '../../lib/fileName';
import { PALETTE } from '../../lib/palette';
import { useRetailerSearch, useFarmerSearch } from '../../hooks/useRecordSearch';

/* ────────────────────────────────────────────────────────────────────────────
 * TODAY'S COVERAGE — "what should I visit today?", answered before anything else.
 *
 * The coverage module tells MANAGERS what is pending. This is the same data turned around for the
 * person who has to walk it: the customers this rep still owes a call, in field-priority order
 * (overdue first, then due soon, nearest first inside each band), each with the one-line context
 * that decides whether to go — how long since the last call, how far, and what they last bought.
 *
 * Reuses `GET /coverage/schedule/due` and the existing start-visit endpoints. No new scheduling
 * logic, no second definition of "due".
 * ──────────────────────────────────────────────────────────────────────────── */
const PRIORITY_STYLE: Record<number, { bar: string; tone: any; label: string }> = {
  0: { bar: 'bg-danger-500', tone: 'red', label: 'Overdue' },
  1: { bar: 'bg-warning-500', tone: 'gold', label: 'Due now' },
  2: { bar: 'bg-sky-500', tone: 'blue', label: 'Upcoming' },
};

function TodaysCoverageCard({
  meId, canStart, busy, onStart,
}: {
  meId?: string; canStart: boolean; busy: string | null;
  onStart: (c: { customerType: string; customerId: string; name: string }) => void;
}) {
  const [here, setHere] = useState<{ lat: number; lng: number } | null>(null);
  const [showAll, setShowAll] = useState(false);

  /*
   * A SOFT location attempt: distances are a convenience, so a refusal or a slow fix must degrade to
   * "no distances" rather than blocking the list or throwing a permission dialog in the rep's face.
   * The hard, mandatory fix still happens at the moment a visit is actually started.
   */
  useEffect(() => {
    if (!navigator.geolocation) return;
    let alive = true;
    navigator.geolocation.getCurrentPosition(
      (p) => { if (alive) setHere({ lat: p.coords.latitude, lng: p.coords.longitude }); },
      () => {},
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300_000 },
    );
    return () => { alive = false; };
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['my-due-today', meId, here?.lat, here?.lng],
    queryFn: () => request<any[]>({
      url: '/coverage/schedule/due',
      params: {
        // All of them: the card shows five and "Show all N", and N was never more than the 40 it asked for.
        userId: meId!, limit: '100000',
        ...(here ? { lat: String(here.lat), lng: String(here.lng) } : {}),
      },
    }),
    enabled: !!meId,
  });

  /*
   * The day's PLAN, read beside the due list (V-09). The due list only knows customers ASSIGNED to this rep, so a
   * stop a Head put on their day for someone else's customer — or for a customer whose rule is already met — never
   * showed here: the card said "Nothing pending" with two stops planned for today.
   */
  const { data: plan, isLoading: planLoading } = useQuery({
    queryKey: ['my-plan-today', meId],
    queryFn: () => request<any[]>({ url: '/coverage/schedule/my-day' }),
    enabled: !!meId,
  });
  const stops = (plan ?? []).filter((s) => s.status !== 'CANCELLED');
  const onPlan = new Set(stops.map((s) => `${s.customerType}-${s.customerId}`));
  const stopsLeft = stops.filter((s) => s.status === 'PLANNED' || s.status === 'STARTED').length;

  // A customer already on today's plan is listed once, under the plan.
  const rows = (data ?? []).filter((r) => !onPlan.has(`${r.customerType}-${r.customerId}`));
  const shown = showAll ? rows : rows.slice(0, 5);
  const overdue = rows.filter((r) => r.priority === 0).length;
  const pending = stopsLeft + rows.length;

  return (
    <Card
      title={<Space><Flag size={16} />Today&apos;s coverage</Space>}
      extra={pending ? <Tag tone={overdue ? 'red' : 'green'}>{pending} pending{overdue ? ` · ${overdue} overdue` : ''}</Tag> : undefined}
      className="mb-4"
    >
      {stops.length > 0 && (
        <div className="mb-3" data-slot="todays-plan">
          <div className="type-label mb-1.5">On today&apos;s plan · {stops.length - stopsLeft} of {stops.length} done</div>
          <div className="space-y-2">
            {stops.map((s, i) => {
              const done = s.status === 'COMPLETED';
              const started = s.status === 'STARTED';
              return (
                <div key={s.id} className="flex gap-2 rounded-lg border border-line bg-surface p-2.5">
                  <span className={`w-1 shrink-0 rounded-full ${done ? 'bg-success-500' : started ? 'bg-sky-500' : 'bg-warning-500'}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`type-body font-semibold wrap-break-word ${done ? 'text-subtle line-through' : ''}`}>{i + 1}. {s.customerName}</span>
                      <Tag tone={done ? 'green' : started ? 'blue' : 'gold'}>{done ? 'Done' : started ? 'In progress' : 'Planned'}</Tag>
                      <Tag tone={s.customerType === 'FARMER' ? 'green' : 'blue'}>{s.customerType === 'FARMER' ? 'Farmer' : 'Retailer'}</Tag>
                    </div>
                    <div className="mt-0.5 type-caption text-subtle">
                      {done && s.checkInAt ? `Visited ${formatIstTime(s.checkInAt)}` : s.place ?? s.territoryName ?? ''}
                      {!done && s.place && s.territoryName ? ` · ${s.territoryName}` : ''}
                      {s.note ? ` · ${s.note}` : ''}
                    </div>
                  </div>
                  {canStart && s.status === 'PLANNED' && (
                    <Button
                      variant="primary" size="sm" icon={<Navigation size={14} />}
                      loading={busy === 'startvisit'}
                      onClick={() => onStart({ customerType: s.customerType, customerId: s.customerId, name: s.customerName })}
                    >
                      Start
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
      {isLoading || planLoading ? (
        <Typography.Paragraph type="secondary">Loading your beat…</Typography.Paragraph>
      ) : !rows.length ? (
        stops.length ? null : (
          <EmptyState
            title="Nothing pending"
            description="Nothing is planned for you today, and every customer assigned to you is covered for their current window."
          />
        )
      ) : (
        <>
          {stops.length > 0 && <div className="type-label mb-1.5">Also due</div>}
          {!here && (
            <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
              Allow location to see how far each customer is.
            </Typography.Paragraph>
          )}
          <div className="space-y-2">
            {shown.map((c, i) => {
              const p = PRIORITY_STYLE[c.priority] ?? PRIORITY_STYLE[2];
              return (
                <div key={`${c.customerType}-${c.customerId}`} className="flex gap-2 rounded-lg border border-line bg-surface p-2.5">
                  <span className={`w-1 shrink-0 rounded-full ${p.bar}`} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="type-body font-semibold">{i + 1}. {c.name}</span>
                      <Tag tone={p.tone}>{p.label}</Tag>
                      {/*
                        * BOTH kinds of customer say what they are, in the colours the rest of the app
                        * uses for them.
                        *
                        * A farmer wore a grey "Farmer" chip and a retailer wore nothing at all, so the
                        * unlabelled rows were the ones a reader had to guess at — and grey is the one
                        * tone that reads as "no category" rather than as a category. Retailer blue and
                        * farmer green match the party badges everywhere else.
                        */}
                      <Tag tone={c.customerType === 'FARMER' ? 'green' : 'blue'}>
                        {c.customerType === 'FARMER' ? 'Farmer' : 'Retailer'}
                      </Tag>
                    </div>
                    <div className="mt-0.5 type-caption text-subtle">
                      {c.lastVisitAt
                        ? `Last visit ${c.daysSinceLast}d ago`
                        : 'Never visited'}
                      {c.distanceKm != null && ` · ${c.distanceKm} km away`}
                      {c.territoryName ? ` · ${c.territoryName}` : ''}
                    </div>
                    <div className="type-caption text-subtle">
                      {c.lastOrderAmount
                        ? `Last order ${inr2(c.lastOrderAmount)}`
                        : c.salesLinked === false ? 'No linked sales' : 'No orders yet'}
                      {/*
                        * "0/2 this window" is the coverage rule in shorthand, and shorthand is what
                        * made it unreadable: two bare numbers over a slash, and "window" — a word the
                        * policy uses internally for its cycle, which means nothing to a rep standing
                        * outside a shop. The policy already computes a human label for the norm
                        * ("2× / month"); said as a sentence, the card can tell them what is left to do
                        * rather than making them work it out.
                        */}
                      {c.required ? (() => {
                        const left = Math.max(0, Number(c.required) - Number(c.done ?? 0));
                        const every = c.normLabel ? ` (${c.normLabel})` : '';
                        return ` · ${left === 0
                          ? `all ${c.required} visits done${every}`
                          : `${left} more visit${left === 1 ? '' : 's'} due${every}`}`;
                      })() : ''}
                    </div>
                  </div>
                  {canStart && (
                    <Button
                      variant="primary" size="sm" icon={<Navigation size={14} />}
                      loading={busy === 'startvisit'}
                      onClick={() => onStart({ customerType: c.customerType, customerId: c.customerId, name: c.name })}
                    >
                      Start
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
          {rows.length > 5 && (
            <Button className="mt-2" block onClick={() => setShowAll(!showAll)}>
              {showAll ? 'Show fewer' : `Show all ${rows.length}`}
            </Button>
          )}
        </>
      )}
    </Card>
  );
}

const SENTIMENTS = [
  { value: 'POSITIVE', label: '🙂 Positive' },
  { value: 'NEUTRAL', label: '😐 Neutral' },
  { value: 'NEGATIVE', label: '🙁 Negative' },
];
const SEVERITIES = ['LOW', 'MED', 'HIGH', 'CRIT'];
const errMsg = (e: any) => e?.response?.data?.error?.message ?? e?.message ?? 'Failed';

export function FieldJourneyPage() {
  const qc = useQueryClient();
  const canRegularize = usePermission('ATTENDANCE.REGULARIZE.REQUEST');
  const navigate = useNavigate();
  // The field journey is for field staff — a Sales Person OR a Sales Head (a head personally
  // works his own beat alongside oversight). Gate by role (a permission gate can't exclude
  // SUPER_ADMIN, who holds the '*' wildcard).
  const role = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);
  const canJourney = role === 'SALES_PERSON' || role === 'SALES_HEAD';

  const { data: myDay, isLoading } = useQuery({
    queryKey: ['my-day'],
    queryFn: () => request<any>({ url: '/ffa/my-day' }),
    enabled: canJourney,
  });
  // The rep's wallet — running balance (+ float outstanding / − reimbursement due).
  const { data: wallet } = useQuery({
    queryKey: ['my-wallet'],
    queryFn: () => request<any>({ url: '/wallets/me' }),
    enabled: canJourney,
  });

  const attendance = myDay?.attendance ?? null;
  const openVisit = myDay?.openVisit ?? null;
  const punchedIn = !!attendance?.checkInAt && !attendance?.checkOutAt;
  const dayCompleted = !!attendance?.checkOutAt;
  // 3-mark lifecycle: an open visit is EN_ROUTE (heading out, not yet arrived) or REACHED (at site).
  const enRoute = !!openVisit && openVisit.visitState === 'EN_ROUTE';
  const atSite = !!openVisit && !enRoute; // REACHED (or a legacy visit that has a check-in)

  // Photos on the open (at-site) visit — for the thumbnail strip + count.
  const { data: openVisitDetail } = useQuery({
    queryKey: ['open-visit-photos', openVisit?.id],
    queryFn: () => request<any>({ url: `/visits/${openVisit.id}` }),
    enabled: !!openVisit && atSite,
  });
  const visitPhotos: any[] = openVisitDetail?.photos ?? [];

  // The rep's own visits for TODAY (completed + reached) — shown as a running record on their field day.
  // Excludes en-route/cancelled (no check-in). Refetches when the open visit changes (i.e. one completes).
  const todayStr = useMemo(() => dayjs().format('YYYY-MM-DD'), []);
  const { data: myVisitsToday } = useQuery({
    queryKey: ['my-visits-today', todayStr, openVisit?.id ?? null],
    queryFn: () => request<any>({ url: '/visits', params: { from: todayStr, to: todayStr } }),
    enabled: canJourney,
  });
  const todaysVisits: any[] = myVisitsToday?.data ?? (Array.isArray(myVisitsToday) ? myVisitsToday : []);

  // Today's expense claim, so the button can say what's actually waiting there — an untouched day, a
  // draft the rep still has to finish, or a claim already gone for approval. Same query key the day
  // modal invalidates, so the label refreshes the moment a line is added.
  const expenseDay = useMemo(() => toIstDateString(new Date()), []);
  const { data: dayClaim } = useQuery({
    queryKey: ['day-claim', expenseDay],
    queryFn: () => request<any>({ url: `/expense-reports/day/${expenseDay}` }),
    enabled: canJourney,
  });
  const claim = dayClaim?.report ?? null;
  const claimIsOpen = !!claim && ['DRAFT', 'CORRECTION_REQUIRED'].includes(claim.status);
  const claimTotal = Number(claim?.subtotalAmount ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
  const expenseLabel = !claim
    ? 'Day expenses'
    : claimIsOpen
      ? `Expense draft · ₹${claimTotal}`
      : `Expenses ${String(claim.status).replace(/_/g, ' ').toLowerCase()}`;

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['my-day'] });
    qc.invalidateQueries({ queryKey: ['my-wallet'] });
    qc.invalidateQueries({ queryKey: ['my-visits-today'] });
    qc.invalidateQueries({ queryKey: ['my-due-today'] });
    qc.invalidateQueries({ queryKey: ['my-plan-today'] });
    /*
     * The shared work counters (navbar badges, My Work, the bell). The app refreshes them after every useMutation,
     * but the marks on this page call the API directly (they need a device fix first), so the badge waited for its
     * 60-second poll after a punch-in, Reached or Leave (V-14).
     */
    for (const queryKey of [['badges'], ['worklist'], ['approvals'], ['notifications-feed']]) {
      qc.invalidateQueries({ queryKey });
    }
  };
  const refreshPhotos = () => qc.invalidateQueries({ queryKey: ['open-visit-photos', openVisit?.id] });
  const errOf = (e: any) => e?.response?.data?.error?.message ?? e?.message ?? 'Failed';

  // ── Location-gated actions ─────────────────────────────────────────────────
  // Every state change needs a FRESH device fix. No fix → the action is blocked and
  // nothing changes (the error surfaces via message.error). No manual fallback for reps.
  const [busy, setBusy] = useState<string | null>(null);
  const withFix = async (key: string, fn: (fix: DeviceFix) => Promise<void>) => {
    setBusy(key);
    try {
      const fix = await getDeviceFix(); // throws if the device can't produce a location
      await fn(fix);
      refresh();
    } catch (e: any) {
      message.error(errOf(e));
    } finally {
      setBusy(null);
    }
  };
  // Common location payload sent with every mark (the server enforces the accuracy floor + mock block).
  const fixData = (fix: DeviceFix) => ({ lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM, isMockLocation: fix.isMockLocation });

  const punchIn = () => withFix('punchin', async (fix) => {
    await request({
      url: '/attendance/check-in', method: 'POST',
      /*
       * No `faceScore`. This used to post a hardcoded 0.93 with every web punch — a number nothing
       * measured, since face matching is not implemented on any platform and the web punch does not
       * even take a photo. It cost twice over: the record dialog rendered the constant as a PASSED
       * integrity check, and the server's `faceScore < FACE_MATCH_THRESHOLD` test could never fire,
       * because the only value it was ever handed was above the threshold by construction.
       * Sending nothing is the truth, and it is what the Android punch already does.
       */
      data: { ...fixData(fix), addressText: `lat ${fix.lat.toFixed(4)}, lng ${fix.lng.toFixed(4)}` },
    });
    message.success('Punched in — attendance marked PRESENT');
  });

  const punchOut = () => withFix('punchout', async (fix) => {
    await request({ url: '/attendance/check-out', method: 'POST', data: fixData(fix) });
    message.success('Punched out — day completed');
  });

  // ── Visit marks 2 & 3 (+ cancel) on the current open visit ──────────────────
  const markReached = () => withFix('reached', async (fix) => {
    await request({ url: `/visits/${openVisit.id}/reached`, method: 'POST', data: fixData(fix) });
    message.success('Reached — you are at the site');
  });
  const cancelVisit = () => withFix('cancel', async (fix) => {
    await request({ url: `/visits/${openVisit.id}/cancel`, method: 'POST', data: fixData(fix) });
    message.success('Visit cancelled');
  });

  // ── Start visit (retailer / farmer) ────────────────────────────────────────
  const [visitKind, setVisitKind] = useState<'RETAILER' | 'FARMER'>('RETAILER');
  const [retailerId, setRetailerId] = useState<string | undefined>();
  const [farmerMode, setFarmerMode] = useState<'existing' | 'new'>('existing');
  const [farmerId, setFarmerId] = useState<string | undefined>();
  const [farmerModalOpen, setFarmerModalOpen] = useState(false);
  const [registeredFarmer, setRegisteredFarmer] = useState<any>(null);
  const [notes, setNotes] = useState('');

  // Searched on the server — a first page of 200 hid every dealer and farmer past it.
  const retailerPick = useRetailerSearch({ key: 'retailers-pick', enabled: canJourney, selected: retailerId });
  const farmerPick = useFarmerSearch({ key: 'farmers-pick', enabled: canJourney, selected: farmerId });

  /**
   * Start a visit straight from Today's coverage — same endpoints, same mandatory device fix as the
   * picker below. The rep should not have to find a customer in a dropdown that the system already
   * told them to go and see.
   */
  const startFromCoverage = (c: { customerType: string; customerId: string; name: string }) => {
    if (!myDay?.attendance?.checkInAt) { message.error('Punch in first — a visit needs an open field day.'); return; }
    if (openVisit) { message.error('Close the visit you already have open first.'); return; }
    withFix('startvisit', async (fix) => {
      const base = { lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM, isMockLocation: fix.isMockLocation };
      if (c.customerType === 'RETAILER') {
        await request({ url: '/visits/retailer', method: 'POST', data: { retailerId: c.customerId, ...base } });
      } else {
        await request({ url: '/visits/farmer', method: 'POST', data: { farmerId: c.customerId, ...base } });
      }
      qc.invalidateQueries({ queryKey: ['my-due-today'] });
      message.success(`Heading to ${c.name} — mark Reached when you arrive`);
    });
  };

  const startVisit = () => {
    // Validate before asking for GPS so the user gets a clear, immediate message.
    const fid = visitKind === 'FARMER' ? (farmerMode === 'existing' ? farmerId : registeredFarmer?.id) : undefined;
    if (visitKind === 'RETAILER' && !retailerId) { message.error('Pick a retailer'); return; }
    if (visitKind === 'FARMER' && !fid) { message.error(farmerMode === 'existing' ? 'Pick a farmer' : 'Register the farmer first'); return; }
    withFix('startvisit', async (fix) => {
      const base = { ...fixData(fix), notes: notes || undefined };
      if (visitKind === 'RETAILER') {
        await request({ url: '/visits/retailer', method: 'POST', data: { retailerId, ...base } });
      } else {
        await request({ url: '/visits/farmer', method: 'POST', data: { farmerId: fid, ...base } });
      }
      message.success('Visit started — head to the site, then mark Reached');
      setRetailerId(undefined); setFarmerId(undefined); setRegisteredFarmer(null); setNotes('');
    });
  };

  // ── In-visit modals ────────────────────────────────────────────────────────
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);

  if (!canJourney) {
    return <Alert type="info" title="My Field Day is for field staff (Sales Persons and Sales Heads)." />;
  }

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <Typography.Title level={4}>My Field Day</Typography.Title>

      {/* Hero action bar: today's status (left) + wallet & punch action (right) — fills the width. */}
      <Card className="mb-4">
        {/*
          * ONE ROW: where the day stands, what the money looks like, and the two actions.
          *
          * A fixed 14/10 column split gave the right-hand side less width than its contents needed, so
          * Punch Out — the most consequential control on the page — wrapped onto a line of its own and
          * the row read as two unrelated halves. Flex lets each part take what it needs and keeps them
          * on one line at any normal width, wrapping only when the screen genuinely cannot hold them.
          */}
        <div className="flex flex-wrap items-center justify-between gap-x-8 gap-y-4">
          <div className="min-w-0">
            {dayCompleted ? (
              <Space align="start" size="middle">
                <CheckCircle2 size={24} style={{ color: 'var(--color-brand-600)' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Day completed</div>
                  <Typography.Text type="secondary">In {formatIst(attendance.checkInAt)} · Out {formatIst(attendance.checkOutAt)}</Typography.Text>
                  {/* Several shifts: worked time is their sum, which is less than In→Out — say so. */}
                  {Number(attendance.sessionCount) > 1 && attendance.workedMin != null && (
                    <div><Typography.Text type="secondary">{attendance.sessionCount} shifts · {Math.floor(attendance.workedMin / 60)}h {attendance.workedMin % 60}m worked</Typography.Text></div>
                  )}
                </div>
              </Space>
            ) : punchedIn ? (
              <Space align="start" size="middle">
                <Clock size={24} style={{ color: 'var(--color-harvest-400)' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>On duty</div>
                  {/* The running shift's own start — on a second shift, the day's first punch-in is hours old. */}
                  <Typography.Text type="secondary">
                    Since {formatIst((attendance.sessions ?? []).at(-1)?.checkInAt ?? attendance.checkInAt)}
                    {Number(attendance.sessionCount) > 1 && ` · shift ${attendance.sessionCount}`}
                    {Number(attendance.sessionCount) > 1 && attendance.workedMin != null && ` · ${Math.floor(attendance.workedMin / 60)}h ${attendance.workedMin % 60}m worked before this shift`}
                  </Typography.Text>
                  {Array.isArray(attendance.anomalyFlags) && attendance.anomalyFlags.length > 0 && (
                    <div style={{ marginTop: 4 }}><Space wrap size={4}>{attendance.anomalyFlags.map((f: string) => <Tag key={f} tone="red">{f}</Tag>)}</Space></div>
                  )}
                </div>
              </Space>
            ) : (
              <Space align="start" size="middle">
                <LogIn size={24} style={{ color: 'var(--color-brand-600)' }} />
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>Start your day</div>
                  <Typography.Text type="secondary">Punch in with your current location. Retailer / farmer visits unlock after you punch in.</Typography.Text>
                </div>
              </Space>
            )}
          </div>
          <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-3">
              {/*
                * THE TWO NUMBERS A REP CHECKS, given room to be read.
                *
                * The float was a squeezed chip on a header row that is mostly empty, and what the day
                * has cost so far — the figure that float is being spent against — was hidden inside a
                * button label. Side by side they answer the only money question of a field day: what
                * have I got, and what have I spent.
                */}
              {wallet && (
                <div className="flex items-start gap-6">
                  <div>
                    <div className="flex items-center gap-1.5 type-label text-subtle">
                      <Wallet size={14} /> Wallet {Number(wallet.currentBalance) >= 0 ? 'float' : 'due'}
                    </div>
                    <div className={`mt-0.5 whitespace-nowrap type-metric tabular-nums ${
                      Number(wallet.currentBalance) > 0 ? 'text-brand-600 dark:text-brand-400'
                        : Number(wallet.currentBalance) < 0 ? 'text-danger-600 dark:text-danger-500' : 'text-subtle'
                    }`}>
                      {inr2(wallet.currentBalance ?? 0)}
                    </div>
                  </div>
                  {claim && (
                    <div>
                      <div className="type-label text-subtle">Claimed today</div>
                      <div className="mt-0.5 whitespace-nowrap type-metric tabular-nums text-content">
                        {inr2(claim.subtotalAmount ?? 0)}
                      </div>
                      <div className="type-caption text-subtle">
                        {claimIsOpen ? 'still a draft' : String(claim.status).replace(/_/g, ' ').toLowerCase()}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {/* The day's expenses live on the day, so this is reachable whenever the rep has been on
                  duty today — not only while standing at a customer. */}
              {(punchedIn || dayCompleted) && (
                <Button icon={<Wallet size={16} />} onClick={() => setExpenseOpen(true)}>{expenseLabel}</Button>
              )}
              {dayCompleted ? (
                <Button variant="primary" icon={<LogIn size={16} />} loading={busy === 'punchin'} onClick={punchIn}>Punch In Again</Button>
              ) : punchedIn ? (
                <Popconfirm
                  title="End your day?"
                  description="Ends your current session. You can punch in again later if you head back out."
                  okText="Punch out" okButtonProps={{ danger: true }} cancelText="Stay on duty"
                  disabled={!!openVisit} onConfirm={punchOut}
                >
                  <Button variant="danger" size="lg" icon={<LogOut size={16} />} loading={busy === 'punchout'} disabled={!!openVisit}>Punch Out</Button>
                </Popconfirm>
              ) : (
                <Button variant="primary" size="lg" icon={<LogIn size={16} />} loading={busy === 'punchin'} onClick={punchIn}>Punch In</Button>
              )}
          </div>
        </div>
        {punchedIn && !!openVisit && (
          <Alert style={{ marginTop: 12 }} type="warning"
            title={enRoute ? 'Reach or cancel your current visit before punching out.' : 'Leave your current visit before punching out.'} />
        )}
      </Card>

      {/* What to do today, before the mechanics of doing it. Shown once punched in and not already
          mid-visit — a rep with an open call has one job, and it is not picking the next customer. */}
      {punchedIn && !openVisit && (
        <TodaysCoverageCard
          meId={myId ? String(myId) : undefined}
          canStart={canJourney}
          busy={busy}
          onStart={startFromCoverage}
        />
      )}

      {/* En route (mark 1 done): heading to the site — Reached / Cancel next. */}
      {punchedIn && enRoute && (
        <Card title={<Space><Navigation size={16} />En route</Space>} className="mb-4" data-slot="en-route">
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Heading to">
              {openVisit.party?.name ?? (openVisit.visitType === 'FARMER' ? 'Farmer' : 'Retailer')}
              {' '}<Tag tone={openVisit.visitType === 'FARMER' ? 'green' : 'blue'}>{openVisit.visitType}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Started at">{formatIst(openVisit.startedAt ?? openVisit.createdAt)}</Descriptions.Item>
          </Descriptions>
          <Alert type="info" style={{ marginBottom: 12 }}
            title="Mark Reached when you arrive"
            description="Your travel time is measured from now until you tap Reached. Cancel if the trip is called off before you arrive." />
          <Space wrap size="middle" style={{ width: '100%' }}>
            <Button variant="primary" icon={<Flag size={16} />} loading={busy === 'reached'} onClick={markReached}>
              Reached the site
            </Button>
            <Popconfirm
              title="Cancel this visit?"
              description="Abandons the trip before arrival — nothing is recorded as a completed visit."
              okText="Cancel visit" okButtonProps={{ danger: true }} cancelText="Keep going"
              onConfirm={cancelVisit}
            >
              <Button variant="danger" icon={<Ban size={16} />} loading={busy === 'cancel'} style={{ marginLeft: 'auto' }}>
                Cancel visit
              </Button>
            </Popconfirm>
          </Space>
        </Card>
      )}

      {/* At site (mark 2 done): arrived — capture work, then Leave (mark 3). */}
      {punchedIn && atSite && (
        <Card title={<Space><MapPin size={16} />At site</Space>} className="mb-4">
          <Descriptions size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="Visiting">
              {openVisit.party?.name ?? (openVisit.visitType === 'FARMER' ? 'Farmer' : 'Retailer')}
              {' '}<Tag tone={openVisit.visitType === 'FARMER' ? 'green' : 'blue'}>{openVisit.visitType}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Reached at">{formatIst(openVisit.checkInAt)}</Descriptions.Item>
            <Descriptions.Item label="In geofence">
              {openVisit.checkInWithinGeofence
                ? <Tag tone="green">Yes</Tag>
                : <Tag tone="orange"><AlertTriangle size={12} style={{ marginRight: 4, verticalAlign: -1 }} />Outside — flagged</Tag>}
            </Descriptions.Item>
          </Descriptions>
          {!openVisit.checkInWithinGeofence && (
            <Alert type="warning" style={{ marginBottom: 12 }}
              title="You reached outside the customer's saved location"
              description="This is recorded and flagged for review — the customer's stored coordinates may be off. Nothing is blocked; carry on with the visit." />
          )}
          <Space wrap size="middle" style={{ width: '100%' }}>
            {openVisit.visitType === 'RETAILER' && (
              <Button icon={<ShoppingCart size={16} />}
                onClick={() => navigate(`/orders/new?retailerId=${openVisit.retailerId}&visitId=${openVisit.id}`)}>
                New Order
              </Button>
            )}
            <VisitPhotoCapture visitId={String(openVisit.id)} onAdded={refreshPhotos}
              label={`Add photos${visitPhotos.length ? ` (${visitPhotos.length}/10)` : ''}`} remaining={10 - visitPhotos.length} />
            <Button icon={<MessageSquare size={16} />} onClick={() => setFeedbackOpen(true)}>Feedback</Button>
            <Button icon={<Wrench size={16} />} onClick={() => setComplaintOpen(true)}>Complaint</Button>
            <Button icon={<Wallet size={16} />} onClick={() => setExpenseOpen(true)}>Add expense</Button>
            <Button variant="danger" icon={<LogOut size={16} />} onClick={() => setLeaveOpen(true)}
              style={{ marginLeft: 'auto' }}>
              Leave (check out)
            </Button>
          </Space>
          {visitPhotos.length > 0 && (
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {visitPhotos.map((p: any) => (
                <a key={p.id} href={p.url} target="_blank" rel="noreferrer" title={(p.gpsLat && !p.withinGeofence) ? 'Taken away from the site (flagged)' : 'Geotagged photo'}
                  style={{ position: 'relative', display: 'inline-block' }}>
                  <img src={p.url} alt="visit" style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 8, border: (p.gpsLat && !p.withinGeofence) ? '2px solid #E7B22C' : '1px solid #e5e7eb' }} />
                </a>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Start a visit */}
      {punchedIn && !openVisit && (
        <Card title="Start a visit">
          <RadioGroup
            value={visitKind}
            onChange={(v) => setVisitKind(v as 'RETAILER' | 'FARMER')}
            inline
            className="mb-3"
            options={[
              { value: 'RETAILER', label: 'Retailer' },
              { value: 'FARMER', label: 'Farmer' },
            ]}
          />

          {visitKind === 'RETAILER' ? (
            <Select showSearch optionFilterProp="label" filterOption={false} onSearch={retailerPick.onSearch} style={{ width: '100%', marginBottom: 12 }}
              placeholder="Search a retailer" aria-label="Search a retailer" value={retailerId} onChange={setRetailerId}
              options={retailerPick.rows.map((r: any) => {
                // Deactivated retailers can't be visited (backend blocks it) — disable them.
                const blocked = r.status === 'SUSPENDED' || r.status === 'CLOSED';
                return {
                  value: String(r.id),
                  label: `${r.companyName ?? r.company_name} (${r.code})${blocked ? ` — ${r.status === 'CLOSED' ? 'Closed' : 'Suspended'}` : ''}`,
                  disabled: blocked,
                };
              })} />
          ) : (
            <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }}>
              <RadioGroup
                value={farmerMode}
                onChange={(v) => setFarmerMode(v as 'existing' | 'new')}
                inline
                options={[
                  { value: 'existing', label: 'Existing farmer' },
                  { value: 'new', label: 'Register new' },
                ]}
              />
              {farmerMode === 'existing' ? (
                <Select showSearch optionFilterProp="label" filterOption={false} onSearch={farmerPick.onSearch} style={{ width: '100%' }}
                  placeholder="Search a farmer" aria-label="Search a farmer" value={farmerId} onChange={setFarmerId}
                  notFoundContent={<EmptyState compact title="No farmers" />}
                  options={farmerPick.rows.map((f: any) => {
                    const blocked = f.status === 'INACTIVE';
                    return {
                      value: String(f.id),
                      label: `${f.fullName ?? f.full_name}${f.mobile ? ` · ${f.mobile}` : ''}${blocked ? ' — Inactive' : ''}`,
                      disabled: blocked,
                    };
                  })} />
              ) : registeredFarmer ? (
                <Space>
                  <Tag tone="green">New farmer: {registeredFarmer.fullName}</Tag>
                  <Button size="sm" onClick={() => { setRegisteredFarmer(null); setFarmerModalOpen(true); }}>Change</Button>
                </Space>
              ) : (
                <Button icon={<Plus size={16} />} onClick={() => setFarmerModalOpen(true)}>Register new farmer</Button>
              )}
            </Space>
          )}
          <Input.TextArea rows={2} placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} style={{ marginBottom: 12 }} />
          <Button variant="primary" icon={<Navigation size={16} />} loading={busy === 'startvisit'} onClick={startVisit}>
            Start visit (head out)
          </Button>
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
            Records your departure point now. You’ll mark <b>Reached</b> on arrival, then <b>Leave</b> when you go.
          </Typography.Text>
        </Card>
      )}

      {/* Today's completed visits — a running record of the day's work (excludes en-route/cancelled).
          Shown even after punch-out so the rep can review the day. */}
      {todaysVisits.length > 0 && (
        <Card title={<Space><CheckCircle2 size={16} />Today's visits ({todaysVisits.length})</Space>} className="mb-4">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {todaysVisits.map((v: any) => <TodayVisitRow key={v.id} visit={v} />)}
          </div>
        </Card>
      )}

      {/* Your own attendance history (default: active days; checkbox reveals leave/absent/holiday). */}
      <div style={{ marginBottom: 16 }}><MyAttendanceHistoryCard /></div>
      {/* Forgot to punch? Request a senior to mark that day on your behalf. Sales-Person-only by
          deliberate rule (see the RegularizeRequestSpOnly migration) — a Sales Head reviews these
          rather than raising them, so the card is hidden instead of calling an endpoint they're
          refused, which used to log a 403 on every My Day load for a Head. */}
      {canRegularize && <div style={{ marginBottom: 16 }}><MyRegularizationsCard /></div>}

      {/* Full farmer registration (same rich modal as the Farmers page) used when
          starting a visit to a brand-new farmer. */}
      <FarmerFormModal
        open={farmerModalOpen}
        onClose={() => setFarmerModalOpen(false)}
        onSaved={(f: any) => { setRegisteredFarmer(f); setFarmerModalOpen(false); }}
      />

      {/* Expenses belong to the DAY, not to one visit — a day's fuel and its allowance can't be split
          across the day's calls. The rep builds up today's claim here; a line may still name one of
          today's visits for a cost that genuinely belongs to just that call. */}
      {expenseOpen && (
        <DayExpenseModal
          date={expenseDay}
          visits={todaysVisits.map((v: any) => ({ id: v.id, label: v.party?.name ? `${v.party.name}${v.visitNo ? ` · ${v.visitNo}` : ''}` : `Visit ${v.visitNo ?? v.id}` }))}
          onClose={() => setExpenseOpen(false)}
          onSaved={refresh}
        />
      )}
      {leaveOpen && openVisit && (
        <LeaveModal visit={openVisit} onClose={() => setLeaveOpen(false)} onDone={refresh} />
      )}

      {feedbackOpen && openVisit && (
        <FeedbackModal visit={openVisit} onClose={() => setFeedbackOpen(false)} onDone={refresh} />
      )}
      {complaintOpen && openVisit && (
        <ComplaintModal visit={openVisit} onClose={() => setComplaintOpen(false)} onDone={refresh} />
      )}
    </div>
  );
}

// ── Today's-visit row: compact summary that expands to full detail (timings, geofence, feedback,
//    notes, geotagged photos). Photos are lazy-loaded from the visit detail only when expanded. ──
function TodayVisitRow({ visit: v }: { visit: any }) {
  const [open, setOpen] = useState(false);
  const { data: detail } = useQuery({
    queryKey: ['visit-detail', v.id],
    queryFn: () => request<any>({ url: `/visits/${v.id}` }),
    enabled: open,
  });
  const photos: any[] = detail?.photos ?? [];
  const sent = v.feedbackSentiment;
  const sentTone = (sent === 'POSITIVE' ? 'green' : sent === 'NEGATIVE' ? 'red' : 'gold') as any;
  const sentLabel = sent === 'POSITIVE' ? '🙂 Positive' : sent === 'NEGATIVE' ? '🙁 Negative' : '😐 Neutral';
  const offSite = v.checkInWithinGeofence === false || v.checkInWithinGeofence === 0;
  const dur = v.durationMin;
  const durTxt = dur != null ? (dur >= 60 ? `${Math.floor(dur / 60)}h ${dur % 60}m` : `${dur}m`) : null;
  const travelMin = v.startedAt && v.checkInAt ? Math.max(0, Math.round((new Date(v.checkInAt).getTime() - new Date(v.startedAt).getTime()) / 60000)) : null;

  return (
    <div style={{ border: '1px solid var(--gb-line, #e5e7eb)', borderRadius: 10, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((o) => !o)}
        style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        <span style={{ width: 9, height: 9, borderRadius: '50%', background: v.visitType === 'FARMER' ? '#8B5CF6' : '#0EA5E9', flexShrink: 0 }} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 500 }}>
            {v.party?.name ?? (v.visitType === 'FARMER' ? 'Farmer' : 'Retailer')} <Tag tone={v.visitType === 'FARMER' ? 'green' : 'blue'}>{v.visitType}</Tag>
          </div>
          <div style={{ fontSize: 12, color: 'var(--gb-text-muted)' }}>
            Reached {formatIstTime(v.checkInAt)}{v.checkOutAt ? ` · left ${formatIstTime(v.checkOutAt)}` : ' · at site'}{durTxt ? ` · ${durTxt}` : ''}
          </div>
        </div>
        {(v.photosCount ?? 0) > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 12, color: 'var(--gb-text-muted)' }}><Camera size={14} />{v.photosCount}</span>}
        {!!v.orderPlaced && <Tag tone="green"><ShoppingCart size={12} style={{ verticalAlign: -1, marginRight: 3 }} />Order</Tag>}
        {sent && <Tag tone={sentTone}>{sent === 'POSITIVE' ? '🙂' : sent === 'NEGATIVE' ? '🙁' : '😐'}</Tag>}
        {offSite && <Tag tone="orange"><AlertTriangle size={12} style={{ verticalAlign: -1, marginRight: 3 }} />Off-site</Tag>}
      </button>
      {open && (
        <div style={{ borderTop: '1px solid var(--gb-line, var(--gb-border))', padding: '10px 14px', background: 'var(--gb-surface-2, var(--gb-surface-2))', fontSize: 13 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', marginBottom: 8 }}>
            {v.startedAt && <span><b>Departed</b> {formatIstTime(v.startedAt)}</span>}
            <span><b>Reached</b> {formatIstTime(v.checkInAt)}{travelMin != null ? ` (${travelMin}m travel)` : ''}</span>
            {v.checkOutAt ? <span><b>Left</b> {formatIstTime(v.checkOutAt)}{durTxt ? ` (${durTxt} at site)` : ''}</span> : <span><Tag tone="blue">Still at site</Tag></span>}
            <span><b>Geofence</b> {offSite ? <Tag tone="orange">Outside</Tag> : <Tag tone="green">In</Tag>}</span>
          </div>
          {(sent || v.feedbackRemark) && (
            <div style={{ marginBottom: 8 }}>
              <b>Feedback:</b> {sent && <Tag tone={sentTone}>{sentLabel}</Tag>} {v.feedbackRemark && <span>— {v.feedbackRemark}</span>}
              {!!v.followUpNeeded && v.followUpDate && <span style={{ color: 'var(--gb-text-muted)' }}> · follow-up {dayjs(v.followUpDate).format('DD MMM YYYY')}</span>}
            </div>
          )}
          {!!v.orderPlaced && (
            <div style={{ marginBottom: 8 }}>
              <b>Order:</b>{' '}
              {v.order?.orderNo
                ? <a href={`/orders/${v.order.id ?? v.orderId}`} style={{ fontWeight: 600 }}>{v.order.orderNo}</a>
                : <span>placed</span>}
              {v.order?.grandTotal != null && <span> · <b>Order Value:</b> {inr2(v.order.grandTotal)}</span>}
            </div>
          )}
          {v.notes && <div style={{ marginBottom: 8 }}><b>Notes:</b> {v.notes}</div>}
          <div>
            <b>Photos ({photos.length || v.photosCount || 0})</b>
            {photos.length ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
                {photos.map((p: any) => {
                  const flagged = p.gpsLat && !p.withinGeofence;
                  return (
                    <a key={p.id} href={p.url} target="_blank" rel="noreferrer" title={flagged ? 'Taken away from the site (flagged)' : 'Geotagged photo'} style={{ display: 'inline-block', textDecoration: 'none', color: 'inherit' }}>
                      <img src={p.url} alt="visit" style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 8, border: flagged ? '2px solid #E7B22C' : '1px solid #e5e7eb', display: 'block' }} />
                      <div style={{ fontSize: 10, color: 'var(--gb-text-muted)', marginTop: 3, maxWidth: 88, lineHeight: 1.3 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><Clock size={12} />{formatIstTime(p.takenAt)}</span><br />
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}><MapPinIcon size={12} />{p.gpsLat ? (p.withinGeofence ? 'at site' : 'off-site') : 'no location'}</span>
                      </div>
                    </a>
                  );
                })}
              </div>
            ) : <span style={{ color: 'var(--gb-text-muted)', marginLeft: 6 }}>{(v.photosCount ?? 0) > 0 ? 'Loading…' : 'None'}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── In-visit: structured feedback ─────────────────────────────────────────────
function FeedbackModal({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: () => void }) {
  const [sentiment, setSentiment] = useState<string | undefined>(visit.feedbackSentiment ?? undefined);
  const [remark, setRemark] = useState(visit.feedbackRemark ?? '');
  const [followUp, setFollowUp] = useState<boolean>(!!visit.followUpNeeded);
  const [followUpDate, setFollowUpDate] = useState<Dayjs | null>(visit.followUpDate ? dayjs(visit.followUpDate) : null);

  const save = useConfirmedMutation({ title: "Send this feedback?", okText: "Yes, send" }, {
    mutationFn: () => request({
      url: `/visits/${visit.id}/feedback`, method: 'PATCH',
      data: {
        sentiment, remark: remark || undefined,
        followUpNeeded: followUp,
        followUpDate: followUp && followUpDate ? followUpDate.format('YYYY-MM-DD') : undefined,
      },
    }),
    onSuccess: () => { message.success('Feedback saved'); onDone(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Visit feedback"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>Save</Button>
      </>}
    >
      <div style={{ marginBottom: 6 }}>Sentiment</div>
      <Select style={{ width: '100%', marginBottom: 12 }} placeholder="Select" value={sentiment} onChange={setSentiment} options={SENTIMENTS} />
      <div style={{ marginBottom: 6 }}>Remark</div>
      <Input.TextArea rows={3} value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Notes from the visit" style={{ marginBottom: 12 }} />
      <Space wrap>
        <Switch checked={followUp} onCheckedChange={setFollowUp} /> Follow-up needed
        {followUp && <DatePicker value={followUpDate} onChange={setFollowUpDate} placeholder="Follow-up date" />}
      </Space>
    </Modal>
  );
}

/**
 * In-visit: raise a complaint.
 *
 * Categories come from the master, not a list typed into this file. The old hard-coded
 * `['GENERAL','QUALITY','PACKAGING',…]` matched nothing in the master, so every complaint a rep
 * raised from a visit would now be refused — and, more to the point, a rep standing in the dealer's
 * shop is the one person who can actually photograph the damaged pack, so this is the LAST place
 * that should be exempt from the evidence rule.
 */
function ComplaintModal({ visit, onClose, onDone }: { visit: any; onClose: () => void; onDone: () => void }) {
  const [categoryId, setCategoryId] = useState('');
  const [severity, setSeverity] = useState('MED');
  const [desc, setDesc] = useState('');
  const [batchNo, setBatchNo] = useState('');
  const [files, setFiles] = useState<string[]>([]);

  const { data: cats } = useQuery({
    queryKey: ['complaint-categories'],
    queryFn: () => request<any[]>({ url: '/complaint-categories' }),
  });
  const rule = (cats ?? []).find((c: any) => String(c.id) === String(categoryId));
  const needed = rule?.requiresAttachment ? Math.max(1, rule.minAttachments || 1) : 0;
  const shortBy = Math.max(0, needed - files.length);
  // The category's default urgency is the floor the server enforces; adopt it so the rep is not
  // silently overruled after submitting.
  useEffect(() => { if (rule) setSeverity(rule.defaultSeverity); }, [rule?.id]);

  const save = useConfirmedMutation({ title: "Raise this complaint?", content: "It is logged and routed to whoever handles this category.", okText: "Yes, raise it" }, {
    mutationFn: () => request({
      url: '/complaints', method: 'POST',
      data: {
        source: 'SALES', categoryId, severity, description: desc,
        batchNo: batchNo.trim() || undefined,
        attachments: files.length ? files : undefined,
        visitId: String(visit.id),
        retailerId: visit.visitType === 'RETAILER' ? String(visit.retailerId) : undefined,
        farmerId: visit.visitType === 'FARMER' && visit.farmerId ? String(visit.farmerId) : undefined,
      },
    }),
    onSuccess: (r: any) => { message.success(`Complaint ${r?.complaintNo ?? ''} created`); onDone(); onClose(); },
    onError: (e: any) => message.error(e?.response?.data?.error?.message ?? 'Failed'),
  });

  const blocked = !categoryId || desc.trim().length < 10 || shortBy > 0
    || (rule?.requiresBatch && !batchNo.trim());

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Raise complaint"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={blocked} loading={save.isPending} onClick={() => save.mutate()}>
          {shortBy > 0 ? `Attach ${shortBy} more` : 'Submit'}
        </Button>
      </>}
    >
      <Space style={{ marginBottom: 12 }} wrap>
        <Select
          value={categoryId || undefined} onChange={(v) => setCategoryId(String(v))}
          style={{ width: 190 }} placeholder="What is this about?"
          options={(cats ?? []).map((c: any) => ({ value: String(c.id), label: c.name }))}
        />
        <Select value={severity} onChange={setSeverity} style={{ width: 120 }} options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
      </Space>
      <Input.TextArea rows={4} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Describe the issue (min 10 chars)" />
      {rule?.requiresBatch && (
        <Input className="mt-2" value={batchNo} onChange={(e) => setBatchNo(e.target.value)}
          placeholder="Batch number printed on the pack (required)" />
      )}
      {needed > 0 && (
        <Alert
          className="mt-2"
          type={shortBy > 0 ? 'warning' : 'success'}
          title={shortBy > 0 ? `${shortBy} photo${shortBy === 1 ? '' : 's'} still needed` : 'Evidence attached'}
          description={rule?.attachmentHint ?? 'Attach a clear photo of the problem.'}
        />
      )}
      <div className="mt-2">
        {files.length > 0 && (
          <Space size={6} wrap className="mb-2">
            {files.map((u, i) => (
              <Tag key={u} closable onClose={() => setFiles((a) => a.filter((x) => x !== u))}>{fileLabel(u, { index: i + 1 })}</Tag>
            ))}
          </Space>
        )}
        <FileUploadField
          category="attachment" buttonLabel={needed > 0 ? 'Add photo' : 'Attach file'} preview={false}
          value="" onChange={(url) => { if (url) setFiles((a) => [...a, url]); }}
        />
      </div>
      <Divider style={{ margin: '12px 0 0' }} />
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>Linked to this visit and the {visit.visitType.toLowerCase()}.</Typography.Text>
    </Modal>
  );
}

// Leaving is now just a confirmation — expenses are logged via the "Add expense"
// button during the visit, so check-out has a single job: stamp the leave time + GPS.
function LeaveModal({ visit, onClose, onDone }: { visit: any; advance?: any; onClose: () => void; onDone: () => void }) {
  const [busy, setBusy] = useState(false);

  const leave = async () => {
    setBusy(true);
    try {
      const fix = await getDeviceFix(); // fresh device fix required to leave, too
      await request({
        url: `/visits/${visit.id}/check-out`, method: 'POST',
        data: { lat: fix.lat, lng: fix.lng, accuracyM: fix.accuracyM, isMockLocation: fix.isMockLocation },
      });
      message.success('Left the site — visit completed');
      onDone(); onClose();
    } catch (e) { message.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <Modal
      open onOpenChange={(o) => { if (!o) onClose(); }} title="Leave this visit?"
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="danger" loading={busy} onClick={leave}>Leave (check out)</Button>
      </>}
    >
      <Typography.Paragraph style={{ marginBottom: 8 }}>
        You'll be checked out of <b>{visit.party?.name ?? String(visit.visitType ?? '').toLowerCase()}</b> and your leave
        time &amp; location will be recorded.
      </Typography.Paragraph>
      <Alert type="info"
        title="Expenses can be added later too"
        description='No need to log expenses now — add any travel / food / fuel costs here, or later from the Visits page, anytime within 7 days of the visit.' />
    </Modal>
  );
}
